/**
 * Gestione centralizzata degli errori HTTP (interceptor di response di API.js).
 *
 * Il backend scopa ogni risorsa sul partner del token e applica le regole di
 * dominio lato server; i codici che il gestionale riceve:
 *   401 -> sessione scaduta/non valida     -> pulizia store + redirect al login
 *   403 -> risorsa altrui / capability      -> toast (messaggio del server se c'è)
 *   404 -> id di un altro partner (CRUD)    -> toast
 *   400 -> regola di dominio (limite prenotazioni, foto-gate, edit-lock...) -> toast col messaggio del server
 *   409 -> conflitto (es. mail già usata, tratta chiusa)                     -> toast col messaggio del server
 *   410 -> servizio dismesso (Escort/Delivery)                                -> toast
 *   5xx / rete -> toast generico
 *
 * L'errore viene SEMPRE ri-lanciato: i catch delle pagine continuano a
 * funzionare (es. per rollback di stato), ma non devono più occuparsi del toast.
 * Per una chiamata che gestisce il messaggio da sola: API.get(url, { skipErrorToast: true }).
 * Il 401 arriva qui SOLO dopo che API.js ha già tentato (e fallito) il refresh.
 */
import { ElMessage } from 'element-plus';
import { forceLogout } from './session';

const GENERIC_BY_STATUS = {
  400: 'Richiesta non valida.',
  401: 'Sessione scaduta: effettua di nuovo il login.',
  403: 'Operazione non consentita: la risorsa non appartiene al tuo partner o la funzione non è abilitata.',
  404: 'Risorsa non trovata (o non appartenente al tuo partner).',
  409: 'Operazione in conflitto con lo stato attuale.',
  410: 'Servizio dismesso: non è più possibile crearlo.',
  500: 'Errore interno del server. Riprova tra qualche istante.',
  502: 'Server temporaneamente non raggiungibile.',
  503: 'Server temporaneamente non disponibile.',
};

/** Estrae il messaggio leggibile dal body NestJS ({ message: string | string[] }). */
export function extractServerMessage(error) {
  const data = error?.response?.data;
  if (!data) return null;
  const msg = data.message ?? data.error;
  if (Array.isArray(msg)) return msg.filter(Boolean).join(' • ');
  if (typeof msg === 'string' && msg.trim()) return msg;
  return null;
}

/** Messaggio utente per un errore axios (server message se sensato, altrimenti generico). */
export function messageForError(error) {
  if (!error?.response) {
    return 'Impossibile contattare il server: controlla la connessione.';
  }
  const { status } = error.response;
  const serverMsg = extractServerMessage(error);
  // Sui 4xx di dominio (400/403/409/410) il backend parla già italiano: lo mostriamo.
  // Idem sul 503, che il backend usa per i servizi esterni non disponibili
  // (es. "Servizio mappe non disponibile (limite di richieste del provider
  // raggiunto): riprova tra qualche minuto"): dice all'utente cosa è successo e
  // che ha senso riprovare, cosa che il generico non fa.
  // Su 401, 404 (i CRUD rispondono "Booking not found" e simili) e sugli altri
  // 5xx il messaggio del server è tecnico/inglese: usiamo il generico italiano.
  const useServerMsg = status !== 401 && status !== 404 && (status < 500 || status === 503);
  if (serverMsg && useServerMsg) {
    // evita i default inglesi di Nest ("Forbidden resource", "Not Found"...)
    const isNestDefault = /^(forbidden resource|forbidden|not found|bad request|unauthorized|conflict|gone)$/i.test(serverMsg);
    if (!isNestDefault) return serverMsg;
  }
  return GENERIC_BY_STATUS[status] ?? `Errore inatteso (${status}).`;
}

const isAuthCall = (config) => /\/auth\/(login|refresh|reset-password)/.test(config?.url ?? '');

/** Handler da registrare in API.interceptors.response.use(undefined, handleHttpError). */
export async function handleHttpError(error) {
  const config = error?.config ?? {};
  const status = error?.response?.status;

  if (status === 401 && !isAuthCall(config)) {
    ElMessage({ message: GENERIC_BY_STATUS[401], type: 'warning', grouping: true, duration: 4000 });
    await forceLogout();
    return Promise.reject(error);
  }

  // Login/refresh/reset: la pagina mostra già i propri messaggi (credenziali errate ecc.)
  if (!config.skipErrorToast && !isAuthCall(config)) {
    ElMessage({
      message: messageForError(error),
      type: status && status < 500 ? 'warning' : 'error',
      grouping: true,
      duration: 5000,
      showClose: true,
    });
  }
  return Promise.reject(error);
}
